(() => {
  if (window.RavinSidewaysDeckLoaded) return;
  window.RavinSidewaysDeckLoaded = true;

  const instances = new WeakMap();
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const pad = value => String(value).padStart(2, '0');
  const ease = t => 1 - Math.pow(1 - t, 3);

  function init(root) {
    if (!root || instances.has(root)) return;
    const stage = root.querySelector('[data-ravin-sideways-stage]');
    const scene = root.querySelector('.ravin-sideways__scene');
    const cards = Array.from(root.querySelectorAll('[data-ravin-sideways-card]'));
    if (!stage || !scene || !cards.length) return;

    const currentText = root.querySelector('[data-ravin-sideways-current]');
    const status = root.querySelector('[data-ravin-sideways-status]');
    const prev = root.querySelector('[data-ravin-sideways-prev]');
    const next = root.querySelector('[data-ravin-sideways-next]');
    const mobile = window.matchMedia('(max-width: 749px)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    const swipeEnabled = root.dataset.ravinSidewaysSwipe === 'true';
    const duration = 520;
    let position = 0;
    let target = 0;
    let focus = -1;
    let frame = 0;
    let startedAt = 0;
    let from = 0;
    let to = 0;
    let touch = null;
    let pointer = null;
    let blockProductClickUntil = 0;

    function paint(card, x, z, tilt, yaw, scale, alpha, layer) {
      const style = card.style;
      style.setProperty('--ravin-sideways-x', x.toFixed(2) + 'px');
      style.setProperty('--ravin-sideways-z', z.toFixed(2) + 'px');
      style.setProperty('--ravin-sideways-tilt', tilt.toFixed(2) + 'deg');
      style.setProperty('--ravin-sideways-yaw', yaw.toFixed(2) + 'deg');
      style.setProperty('--ravin-sideways-scale', scale.toFixed(4));
      style.setProperty('--ravin-sideways-alpha', alpha.toFixed(3));
      style.zIndex = String(layer);
    }

    function render() {
      const focused = clamp(Math.round(position), 0, cards.length - 1);
      const viewport = Math.max(1, scene.clientWidth);
      root.style.setProperty('--ravin-sideways-progress',
        (cards.length > 1 ? position / (cards.length - 1) : 1).toFixed(5));
      if (focused !== focus) {
        if (currentText) currentText.textContent = pad(focused + 1);
        cards.forEach((card, index) => {
          const selected = index === focused;
          card.classList.toggle('is-active', selected);
          card.toggleAttribute('inert', !selected);
          if (selected) card.removeAttribute('aria-hidden');
          else card.setAttribute('aria-hidden', 'true');
        });
        focus = focused;
      }

      cards.forEach((card, index) => {
        const delta = index - position;
        const d = Math.abs(delta);
        if (reduced.matches) {
          paint(card, 0, 150, 0, 0, 1, index === focused ? 1 : 0,
            index === focused ? 1000 : 1);
          return;
        }
        const x = delta * viewport * (mobile.matches ? .81 : .75);
        const z = 155 - Math.min(d, 3) * 190;
        const yaw = -Math.sign(delta) * Math.min(d, 1) * 64;
        const tilt = Math.sign(delta) * Math.min(d, 1) * 3;
        const scale = 1 - Math.min(.28, d * .11);
        const alpha = clamp(1 - Math.max(0, d - .3) * .68, 0, 1);
        const layer = 1000 - Math.round(d * 100);
        paint(card, x, z, tilt, yaw, scale, alpha, layer);
      });
    }

    function updateControls(announce = false) {
      if (prev) prev.disabled = target <= 0;
      if (next) next.disabled = target >= cards.length - 1;
      if (announce && status) {
        const selectedTitle = cards[target]?.querySelector('.ravin-sideways__card-title')?.textContent?.trim() || 'Fashion card';
        status.textContent = selectedTitle + ', card ' + (target + 1) + ' of ' + cards.length;
      }
    }

    function stopAnimation() {
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
    }

    function navigateTo(index, immediate = false) {
      const nextIndex = clamp(index, 0, cards.length - 1);
      if (nextIndex === target && !immediate) return;
      stopAnimation();
      target = nextIndex;
      updateControls(true);
      if (reduced.matches || immediate) {
        position = target;
        render();
        return;
      }
      from = position;
      to = target;
      startedAt = performance.now();
      const tick = now => {
        const progress = clamp((now - startedAt) / duration, 0, 1);
        position = from + (to - from) * ease(progress);
        render();
        if (progress < 1) frame = window.requestAnimationFrame(tick);
        else {
          frame = 0;
          position = to;
          render();
        }
      };
      frame = window.requestAnimationFrame(tick);
    }

    function navigate(step) {
      if (cards.length < 2) return;
      navigateTo(target + step);
    }

    if (prev) prev.addEventListener('click', () => navigate(-1), {signal:controller.signal});
    if (next) next.addEventListener('click', () => navigate(1), {signal:controller.signal});

    stage.addEventListener('click', event => {
      if (Date.now() < blockProductClickUntil && event.target.closest('.ravin-sideways__card-link')) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, {capture:true, signal:controller.signal});

    const findTouch = (list, id) => {
      for (const item of list) if (item.identifier === id) return item;
      return null;
    };

    if (swipeEnabled && cards.length > 1) {
      stage.addEventListener('touchstart', event => {
        if (event.touches.length !== 1 || event.target.closest('button')) {
          touch = null;
          return;
        }
        const finger = event.touches[0];
        touch = {
          id:finger.identifier, x:finger.clientX, y:finger.clientY,
          horizontal:false, vertical:false
        };
      }, {passive:true,signal:controller.signal});

      stage.addEventListener('touchmove', event => {
        if (!touch || event.touches.length !== 1) {
          touch = null;
          return;
        }
        const finger = findTouch(event.touches,touch.id);
        if (!finger) return;
        const dx = finger.clientX - touch.x, dy = finger.clientY - touch.y;
        if (!touch.horizontal && !touch.vertical && Math.abs(dy) > 11 &&
          Math.abs(dy) > Math.abs(dx) * 1.16) touch.vertical = true;
        if (touch.vertical) return;
        if (!touch.horizontal && Math.abs(dx) > 11 &&
          Math.abs(dx) > Math.abs(dy) * 1.2) touch.horizontal = true;
        if (touch.horizontal && event.cancelable) event.preventDefault();
      }, {passive:false,signal:controller.signal});

      stage.addEventListener('touchend', event => {
        if (!touch) return;
        const finger = findTouch(event.changedTouches,touch.id);
        if (!finger) return;
        const start = touch;
        touch = null;
        const dx = finger.clientX - start.x, dy = finger.clientY - start.y;
        if (start.vertical || Math.abs(dx) < 38 || Math.abs(dx) <= Math.abs(dy) * 1.2) return;
        blockProductClickUntil = Date.now() + 550;
        navigate(dx < 0 ? 1 : -1);
      }, {passive:true,signal:controller.signal});

      stage.addEventListener('touchcancel',()=>{touch=null;},{passive:true,signal:controller.signal});

      stage.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch' || event.button !== 0 || event.target.closest('button')) return;
        pointer = {id:event.pointerId,x:event.clientX,y:event.clientY};
      }, {signal:controller.signal});
      stage.addEventListener('pointerup', event => {
        if (!pointer || event.pointerId !== pointer.id) return;
        const start=pointer;
        pointer=null;
        const dx=event.clientX-start.x,dy=event.clientY-start.y;
        if (Math.abs(dx) < 38 || Math.abs(dx) <= Math.abs(dy) * 1.2) return;
        blockProductClickUntil = Date.now() + 550;
        navigate(dx < 0 ? 1 : -1);
      }, {signal:controller.signal});
      for (const type of ['pointercancel','lostpointercapture']) {
        stage.addEventListener(type,()=>{pointer=null;},{signal:controller.signal});
      }
    }

    root.addEventListener('keydown', event => {
      if (!['ArrowLeft','ArrowRight'].includes(event.key) ||
          event.target.closest('input,textarea,select,[contenteditable="true"]')) return;
      event.preventDefault();
      navigate(event.key === 'ArrowRight' ? 1 : -1);
    }, {signal:controller.signal});

    root.addEventListener('shopify:block:select', event => {
      const card=event.target.closest('[data-ravin-sideways-card]');
      if (!card || !root.contains(card)) return;
      const index=cards.indexOf(card);
      if (index >= 0) navigateTo(index,true);
    }, {signal:controller.signal});

    window.addEventListener('resize',render,{passive:true,signal:controller.signal});
    window.addEventListener('pageshow',render,{signal:controller.signal});
    reduced.addEventListener?.('change',()=>{
      stopAnimation();
      position=target;
      render();
    },{signal:controller.signal});

    instances.set(root,()=>{
      stopAnimation();
      controller.abort();
      instances.delete(root);
    });
    updateControls();
    render();
  }

  function initIn(scope) {
    if (scope.matches?.('[data-ravin-3d-sideways-deck]')) init(scope);
    scope.querySelectorAll?.('[data-ravin-3d-sideways-deck]').forEach(init);
  }
  function unloadIn(scope) {
    const all=[];
    if (scope.matches?.('[data-ravin-3d-sideways-deck]')) all.push(scope);
    scope.querySelectorAll?.('[data-ravin-3d-sideways-deck]').forEach(x=>all.push(x));
    all.forEach(x=>instances.get(x)?.());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded',()=>initIn(document),{once:true});
  } else initIn(document);
  document.addEventListener('shopify:section:load',event=>initIn(event.target));
  document.addEventListener('shopify:section:unload',event=>unloadIn(event.target));
})();